import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import type { ChatAvailableCircle } from "@core/features/chat";

export type Props = {
  /** Circles the user can choose to chat with in the active program. */
  availableCircles: ChatAvailableCircle[];
  /** Fired with the selected circle's id when the user clicks a row. */
  onSelect: (circleId: number) => void;
};

/**
 * The expanded "you must pick" UI shown inside the chat sheet when a
 * multi-circle user opens chat for the first time (or after their prior
 * selection became stale). Replaces the conversation surface entirely
 * until the user picks; once picked, the selection is persisted and the
 * sheet returns to a single-conversation view with the
 * change-circle affordance in the header.
 *
 * Flat list of circle names — users are in a small N of circles and the
 * list never needs grouping or search at this scale.
 */
export function ChatCirclePicker({ availableCircles, onSelect }: Props) {
  return (
    <div
      className="flex flex-col gap-6 px-4 py-8"
      data-testid="chat-circle-picker"
    >
      <div className="flex flex-col gap-2">
        <Type variant="h4" as="h3">
          Pick a chat group
        </Type>
        <Type variant="body2">
          You're in more than one chats in this program. Choose where you want
          to start the conversation — you can switch any time from the chat
          header.
        </Type>
      </div>
      <ul className="flex flex-col gap-2" role="list">
        {availableCircles.map((circle) => (
          <li key={circle.circle_id}>
            <Button
              variant="secondary"
              onClick={() => onSelect(circle.circle_id)}
              data-testid={`chat-circle-picker-option-${circle.circle_id}`}
              className="justify-start"
            >
              {circle.name}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ChatCirclePicker;
